<template>
  <div class="skillBar microskillSidebar" :class="[phase, capacity, skillType, infoStyleClass]" ref="bar">
    <div class="skill">
      <section class="skillBarContent">
        <div class="sizeChecker" aria-hidden="true">
          <div class="info" ref="infoHolder">
            <phase-info key="info" class="phaseInfo" />
          </div>
          <div class="extra" ref="extrasHolder">
            <div class="fauxStartWith" v-if="showStartWith">
              <b>Start with&hellip;</b>
              <p>Person Name</p>
            </div>
            <meeting-info
              v-bind="$attrs"
              v-if="showProposal"
              key="skillBarProposal"
              class="proposal extraItem"
              :field="proposal"
              :edited-by="editing.proposal"
              expanded="true"
              name="proposal"
              default-text=""
              type="paragraph"
              optional="true"
              :heading="proposalHeading"
            />

            <meeting-info
              class="extraItem"
              v-bind="$attrs"
              v-if="showCheckInQuestion"
              :field="checkInQuestions"
              :edited-by="phaseStore.editing.question"
              expanded="true"
              name="checkInQuestion"
              default-text=""
              type="list"
              optional="true"
              :heading="checkInHeading"
            />

            <meeting-info
              class="extraItem"
              v-bind="$attrs"
              v-if="showTopic"
              :field="phaseStore.topic"
              :edited-by="phaseStore.editing.topic"
              expanded="true"
              name="topic"
              default-text=""
              type="paragraph"
              optional="true"
              :heading="topicHeading"
            />
          </div>
        </div>
        <transition-group name="skillbar-bits" class="skillBarText real" ref="textHolder" :class="barResizeClass" tag="section">
          <header key="header" ref="header">
            <app-icon :icon="phase" class="currentPhaseIcon phaseIcon" key="currentPhase" :data-icon-name="phase" />
            <user-menu key="user" />
          </header>
          <div class="infoHolder" key="info">
            <phase-info key="info" class="phaseInfo" />
          </div>
          <div class="extras" key="extras">
            <start-with v-if="showStartWith" class="extraItem" />

            <meeting-info
              v-bind="$attrs"
              v-if="showProposal"
              key="skillBarProposal"
              class="proposal extraItem"
              :field="proposal"
              :edited-by="editing.proposal"
              expanded="true"
              name="proposal"
              default-text=""
              type="paragraph"
              optional="true"
              :heading="proposalHeading"
            />

            <meeting-info
              class="extraItem"
              v-bind="$attrs"
              v-if="showCheckInQuestion"
              key="skillBarCheckInQuestion"
              :field="checkInQuestions"
              :edited-by="editing.question"
              expanded="true"
              name="checkInQuestion"
              default-text=""
              type="list"
              optional="true"
              :heading="checkInHeading"
            />

            <meeting-info
              class="extraItem"
              v-bind="$attrs"
              v-if="showTopic"
              key="skillBarTopic"
              :field="topic"
              :edited-by="editing.topic"
              expanded="true"
              name="topic"
              default-text=""
              type="paragraph"
              optional="true"
              :heading="topicHeading"
            />

            <div id="sidebarExtra" class="phaseExtra extraItem" key="extra"></div>
          </div>
        </transition-group>

        <!-- this is where we teleport stuff -->

        <timer-responsive
          class="timerHolder"
          :class="[extraAnimating || resizing ? 'isResizing' : '']"
          v-if="showTimer"
          key="timers"
          type="phase"
          :barType="skillType"
        />
        <microskill-sidebar-buttons key="buttons" class="buttons actions" :class="[resizing ? 'isResizing' : '']" />
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue';
import { useElementSize, debouncedWatch } from '@vueuse/core';
import _ from 'underscore';
import { storeToRefs } from 'pinia';
import { usePhaseStore } from '@/store/pinia/phase';
import { useSidebarStore } from '@/store/pinia/sidebar';
import { useUsersStore } from '@/store/pinia/users';
import { useFrameStore } from '@/store/pinia/frame';
import TimerResponsive from '@/components/TimerResponsive.vue';
import MeetingInfo from '@/components/MeetingInfo';
import MicroskillSidebarButtons from '@/components/MicroskillSidebarButtons';
import UserMenu from '@/components/MicroskillSidebarUsers';
import PhaseInfo from '@/components/MicroskillSidebarPhaseInfo';
import StartWith from '@/components/StartWith.vue';
import AppIcon from '@/components/AppIcon.vue';
import screenInfo from '@/resources/screen-info';
import { useBodyClasses } from '../composables/useBodyClasses';

const phaseStore = usePhaseStore();
const sidebarStore = useSidebarStore();
const usersStore = useUsersStore();
const frameStore = useFrameStore();
const { changeBodyClass } = useBodyClasses();
const { resizing } = storeToRefs(frameStore);

const topButton = ref(true);
const editRequest = ref(false);
const infoStyleClass = ref(['checkingSize', 'noHeader']);
const header = ref(null);
const infoHolder = ref(null);
const textHolder = ref(null);
const extrasHolder = ref(null);
const bar = ref(null);
const barResizeClass = ref('');
const { height: barHeight } = useElementSize(bar);

onMounted(() => {
  nextTick(() => {
    topButton.value = topButtonRaw?.value;
  });
});

const { iAmStillAway, iAmStillAwayInGroup } = storeToRefs(usersStore);
const { current: currentPhase, extraAnimating } = storeToRefs(sidebarStore);
const { phaseStage, questions, editing, setupPhase, topic, proposal } = storeToRefs(phaseStore);
const isResizing = computed(() => resizing.value || extraAnimating.value);

let resizingInterval = null;

watch(isResizing, (newVal) => {
  if (newVal) {
    clearTimeout(resizingInterval);
    barResizeClass.value = 'isResizing';
  } else {
    resizingInterval = setTimeout(() => {
      barResizeClass.value = '';
    }, 300);
  }
});

const phase = computed(() => {
  if (iAmStillAway.value) {
    return 'breakTime';
  } else if (iAmStillAwayInGroup.value) {
    return 'groups';
  } else {
    return currentPhase.value.phase;
  }
});

const iAmNotHere = computed(() => iAmStillAway.value || iAmStillAwayInGroup.value);

const skillType = computed(() => currentPhase.value.barType);
const skillCapacity = computed(() => screenInfo[currentPhase.value.phase]?.skill);
const skillBar = computed(() => phase.value);
const capacity = computed(() => (phase.value === 'breakTime' ? 'misc' : skillCapacity.value));
const type = computed(() => (iAmNotHere.value ? 'phase' : skillType.value));
const topButtonRaw = computed(() => skillType.value === 'info');

const showProposal = computed(() => phase.value === 'decision' && skillType.value === 'phase' && phaseStage.value !== 'proposal');
const proposalHeading = computed(() => (proposal.value ? 'Proposal' : 'Add a proposal'));

const showTopic = computed(() => ['dialogue', 'feedback'].includes(skillBar.value) && skillType.value === 'phase' && phaseStage.value !== 'topic');
const topicHeading = computed(() => (skillBar.value === 'feedback' && !topic.value ? 'Add feedback topic...' : 'What are we exploring?'));

const showAddCheckInQuestion = computed(() => {
  return questions.value.length < 1 && !editing.value.questions && ['checkIn', 'checkOut'].includes(skillBar.value) && skillType.value !== 'info';
});
const showCheckInQuestion = computed(() => ['checkIn', 'checkOut'].includes(skillBar.value) && skillType.value !== 'info');
const checkInQuestions = computed(() => questions.value.map((q) => q.description));
const questionOrQuestions = computed(() => (checkInQuestions.value.length > 1 ? 'questions' : 'question'));
const checkInHeading = computed(() => {
  const inOut = skillBar.value === 'checkOut' ? 'Out' : 'In';
  return showAddCheckInQuestion.value ? `Add a Check-${inOut} question...` : `Check-${inOut} ${questionOrQuestions.value}`;
});

const showStartWith = computed(() => skillType.value === 'setTime' && ['checkIn', 'checkOut'].includes(setupPhase.value));
const showTimer = computed(() => skillType.value !== 'info');

watch(topButtonRaw, (newVal) => {
  // Do the cool animation here
  topButton.value = newVal;
});

watch(
  type,
  (nv) => {
    editRequest.value = false;
    if (nv === 'phase') {
      changeBodyClass({ add: 'skillBar-phase', remove: 'skillBar-setup' });
    } else {
      changeBodyClass({ remove: 'skillBar-phase', add: 'skillBar-setup' });
    }
  },
  {
    immediate: true,
  },
);

debouncedWatch(
  barHeight,
  (nv) => {
    setTimeout(() => {
      updateTextSizer(nv);
    }, 50);
  },
  {
    debounce: 100,
    immediate: true,
  },
);

watch(type, () => {
  setTimeout(() => {
    checkResponsiveVisibility();
  }, 300);
});

watch(checkInQuestions, () => {
  checkResponsiveVisibility();
});
watch(proposal, () => {
  checkResponsiveVisibility();
});
watch(topic, () => {
  checkResponsiveVisibility();
});

function checkResponsiveVisibilityRaw(delay = 300) {
  setTimeout(() => {
    updateTextSizer(barHeight.value);
  }, delay);
}

const checkResponsiveVisibility = _.debounce(checkResponsiveVisibilityRaw, 500);

let buttonsAndGap = computed(() => frameStore.pxToRem(146));

function updateTextSizer(barHeight) {
  if (skillType.value === 'info') {
    infoStyleClass.value = ['noExtras'];
    return false;
  }
  const timerAndButtons = frameStore.pxToRem(250) + buttonsAndGap.value;
  const smallTimerAndButtons = frameStore.pxToRem(168) + buttonsAndGap.value;
  let headerHeight = frameStore.pxToRem(83) + frameStore.pxToRem(14);
  let infoHeight = infoHolder?.value?.offsetHeight + frameStore.pxToRem(14);
  let extrasHeight = extrasHolder?.value?.offsetHeight;
  const fullHeight = headerHeight + infoHeight + extrasHeight;
  const headerlessHeight = infoHeight + extrasHeight;
  let newClasses = [];

  let check = barHeight - timerAndButtons;

  if (check < fullHeight) {
    check = barHeight - smallTimerAndButtons;
  }

  if (fullHeight > check) {
    newClasses.push('noHeader');
  }

  if (headerlessHeight > check) {
    newClasses.push('noExtras');
  }

  if (infoHeight > check) {
    newClasses.push('noDescription');
  }

  infoStyleClass.value = newClasses;

  // 242 for the timer, 100 for the buttons, 32 for the padding, 14 for the gap;
}
</script>
<style lang="scss" scoped>
.skillBarContent {
  left: rem(34px);
  right: rem(24px);
  top: rem(32px);

  bottom: 0;
  // right: rem(24px);
  // height: calc(100 * var(--vh) - #{rem($log-height)});
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto minmax(#{rem(170px)}, 1fr) auto;
  padding: 0;
  z-index: 2;
  position: absolute;
  transition: all $sidebar-duration $sidebar-ease-general;
}

.info .skillBarContent {
  grid-template-rows: auto minmax(0, auto) 1fr;
}

.timerHolder {
  grid-row: 2;
}
.skillBarText {
  grid-row: 1;
  transition: opacity 0.3s ease 0.3s;
  z-index: 10;
}

.isResizing.skillBarText {
  opacity: 0;
  transition: none;
  //animation: none;
}

.actions {
  grid-row: 3;
  transition: opacity 0.3s ease;
  &.isResizing {
    opacity: 0;
  }
}

.microskillSidebar {
  container: --container-microskill-sidebar / size;
  @include no-scrollbars;

  header {
    position: relative;
    height: rem(83px);
    z-index: 3;
    display: flex;
    justify-content: flex-start;
    margin-bottom: 1rem;
    transition: transform $sidebar-duration $sidebar-ease-general;
  }

  .currentPhaseIcon {
    z-index: 90;
    justify-content: center;
    align-items: center;
    transition: transform 0.3s ease;
  }

  .extras {
    animation: fadeIn 0.3s ease 1s both;
  }

  .overflowChecker {
    height: 1px;
    background: red;
  }

  :deep(.isEditing) {
    color: var(--c__text);
    :deep(.text) {
      font-weight: 300;
    }
  }

  :deep(.infoDisplayer),
  :deep(.infoHeading) {
    padding: 0;
    color: var(--c__text);
    width: 100%;
  }

  :deep(.infoDisplayer .infoHeading) {
    padding: 0;
  }

  .infoHeading {
    margin-top: 0;
  }

  .checkInQuestion {
    margin-top: 1em;
  }

  .phaseExtra + .checkInQuestion {
    margin-top: 0;
  }

  .proposal,
  .topic {
    margin-top: 1em;
  }
  .checkInQuestion {
    margin-bottom: rem(10px);
  }

  .proposal {
    margin-bottom: rem(10px);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
  }
}

@each $name in $skills {
  .microskillSidebar.#{$name} {
    background: var(--c__skillbar-bg-#{$name});

    .currentPhaseIcon {
      color: var(--c__#{$name});
    }

    :deep(.isEditing) {
      background: var(--c__skillbar-bg-#{$name});
      color: var(--c__text-energy-invert);
    }

    .editTime,
    :deep(.infoSection .edit),
    :deep(.stealthButton) {
      color: var(--c__text-energy-invert);
      background: var(--c__#{$name}-bg-90);
    }
  }
}

.microskillSidebar {
  color: var(--c__text-energy);
  :deep(.skillText) {
    color: var(--c__text-energy);
  }
  :deep(.timerHolder) {
    --timer-base: var(--c__text-energy-invert);
    --timer-bar: var(--c__text-energy-invert);
    --timer-track: var(--c__text-energy);
    --timer-unit: var(--c__text-energy);
    --timer-minutes: var(--c__text-energy-invert);
    --timer-seconds: var(--c__text-energy);
  }

  .timerHolder {
    animation: growIn 0.3s ease 1s both;
    :deep(.bigTimer),
    :deep(.smallerTimer) {
      transition: transform 0.3s ease 0.5s;
    }
  }

  .isResizing.timerHolder {
    :deep(.bigTimer),
    :deep(.smallerTimer) {
      transition: transform 0.3s ease;
      transform: scale(0);
    }
  }
}

.misc.microskillSidebar {
  color: var(--c__text-energy);
  :deep(.skillText) {
    color: var(--c__text-energy);
  }
  :deep(.timerHolder) {
    --timer-base: var(--c__accent);
    --timer-bar: var(--c__accent);
    --timer-track: var(--c__text-energy);
    --timer-unit: var(--c__text-energy);
    --timer-minutes: var(--c__text-energy);
    --timer-seconds: var(--c__text-energy);
  }
}

.microskillSidebar.dialogue {
  :deep(.isEditing),
  :deep(.infoSection .edit),
  :deep(.stealthButton) {
    color: var(--c__accent);
  }
}

.endSkill .sidebarTimer {
  @media only screen and (min-height: $sidebar-timer-center-break) {
    margin: auto;
  }
}

// .endSkill .sidebarTimer,
// .misc .skill .sidebarTimer {
//   flex: 999 0 auto;
// }

// Transition classes

.skillbar-bits-enter-active,
.skillbar-bits-enter-active.skillbar-bits-move {
  transition: all 0.5s cubic-bezier(0.6, 0, 0, 1);
}

.skillbar-bits-leave-active {
  transition: opacity 0.3s cubic-bezier(0.5, 0, 1, 0.5);
  position: absolute;
}

.skillbar-bits-enter-from,
.skillbar-bits-leave-to {
  opacity: 0;
}

.skillbar-bits-move {
  transition: transform 0.5s cubic-bezier(0.6, 0, 0, 1);
}

.microskillSidebar {
  header,
  header > * {
    transform-origin: 50% 0%;
  }
  header,
  header > *,
  .skillBarContent {
    transition: all $sidebar-duration $sidebar-ease-general;
  }
}

.split .microskillSidebar {
}

// Sizing things

@container --container-microskill-sidebar (height < 30rem) {
  .skillBarContent {
    grid-template-rows: minmax(2rem, auto) minmax(#{rem(170px)}, 1fr) auto;
  }
}
@container --container-microskill-sidebar (height < 23rem) {
  .skillBarContent {
    grid-template-rows: minmax(2rem, auto) minmax(50%, 1fr) auto;
  }
}

.microskillSidebar {
  .sizeChecker {
    position: absolute;
    height: auto;
    opacity: 0;
    background: rgba(0, 0, 0, 0.5);
    pointer-events: none;
    top: rem(97px);
    left: 0;

    .extra {
      padding: 1em 0;
    }
  }
  // &.checkingSize .skillBarText {
  //   position: relative;
  //   & > * {
  //     transition: none;
  //   }
  //   //opacity: 0;
  //   p,
  //   h2 {
  //     left: 0;
  //     right: 0;
  //   }
  // }

  &.noHeader {
    :deep(h2.title) {
      font-family: nowhereRegular;
      text-transform: lowercase;
      @include type-size('large');
    }
    .sizeChecker {
      top: 0;
    }
  }
  &.noHeader .real {
    header {
      transform: scaleY(0);
      opacity: 0;
      height: 0;
      margin-bottom: 0;
    }
    .skillBarContent {
      transition: all $sidebar-duration $sidebar-ease-general;
    }
  }
  &.noExtras .real {
    .extraItem {
      transform: scaleY(0);
      opacity: 0;
      position: absolute;
    }
  }

  &.noDescription {
    :deep(h2.title) {
      text-align: center;
      margin: 0 auto;
    }
  }
  &.noDescription .real {
    :deep(.phaseInfo p) {
      transform: scaleY(0);
      opacity: 0;
      position: absolute;
    }
  }
}

.skillText + .buttons {
  margin-top: rem(30px);
}
</style>
